const SELECTORS = {
  dot: "[data-si-dot]",
};

class ScrollIndicator extends HTMLElement {
  #observer = null;
  #resizeObserver = null;
  #currentIndex = 0;
  #dotElements = [];
  #target = null;
  #items = [];

  connectedCallback() {
    try {
      this.#init();
    } catch (error) {
      console.error("[ScrollIndicator:init]", error);
    }
  }

  #init() {
    const mode = this.dataset.siMode || "scroll";

    if (mode === "scroll") {
      this.#initScrollMode();
    }
  }

  #initScrollMode() {
    const targetId = this.dataset.siTarget;
    if (!targetId) {
      throw new Error("[ScrollIndicator] Missing data-si-target attribute");
    }

    this.#target = document.getElementById(targetId);
    if (!this.#target) return;

    this.#items = [...this.#target.children];
    if (this.#items.length <= 1) return;

    this.#renderDots(this.#items.length);
    this.#observeItems();
    this.#updateVisibility();

    this.#resizeObserver = new ResizeObserver(() => this.#updateVisibility());
    this.#resizeObserver.observe(this.#target);
  }

  #updateVisibility() {
    if (!this.#target) return;
    const isScrollable = this.#target.scrollWidth > this.#target.clientWidth + 1;
    this.hidden = !isScrollable;
  }

  #observeItems() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        let bestEntry = null;
        let bestRatio = 0;

        for (const entry of entries) {
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestEntry = entry;
          }
        }

        if (bestEntry && bestRatio > 0.5) {
          const index = this.#items.indexOf(bestEntry.target);
          if (index !== -1) {
            this.#setActive(index);
          }
        }
      },
      { root: this.#target, threshold: [0, 0.5, 1] },
    );

    for (const item of this.#items) {
      this.#observer.observe(item);
    }
  }

  #renderDots(count) {
    this.innerHTML = "";
    this.#dotElements = [];
    const isManual = (this.dataset.siMode || "scroll") === "manual";

    for (let i = 0; i < count; i++) {
      const dot = document.createElement("button");
      dot.setAttribute("data-si-dot", "");
      dot.setAttribute("type", "button");
      dot.setAttribute("aria-label", `Slide ${i + 1}`);
      dot.classList.add("si-dot");
      if (i === 0) dot.classList.add("is-active");

      dot.addEventListener("click", () => {
        if (isManual) {
          this.dispatchEvent(
            new CustomEvent("si:select", { detail: { index: i } }),
          );
        } else {
          this.#scrollToItem(i);
        }
      });

      this.appendChild(dot);
      this.#dotElements.push(dot);
    }
  }

  #scrollToItem(index) {
    const item = this.#items[index];
    if (!item || !this.#target) return;

    const containerRect = this.#target.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    this.#target.scrollTo({
      left: this.#target.scrollLeft + itemRect.left - containerRect.left,
      behavior: "smooth",
    });
  }

  #setActive(index) {
    if (index === this.#currentIndex) return;
    this.#currentIndex = index;
    this.#updateDots();
  }

  #updateDots() {
    for (let i = 0; i < this.#dotElements.length; i++) {
      this.#dotElements[i].classList.toggle(
        "is-active",
        i === this.#currentIndex,
      );
    }
  }

  setIndex(index) {
    if (index < 0 || index >= this.#dotElements.length) return;
    this.#setActive(index);
  }

  setCount(count) {
    this.#renderDots(count);
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }
}

if (!customElements.get("scroll-indicator")) {
  customElements.define("scroll-indicator", ScrollIndicator);
}